import {
  getMerchant,
  deleteMerchant,
  updateMerchant,
  addMerchant,
} from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { IMerchant, IMerchantRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Flex,
  Form,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';
import dayjs from 'dayjs';

const _modal: IDataModal<IMerchant> = {
  open: false,
};

const MerchantPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IMerchant[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<IMerchant[]>([]);
  const [modal, setModal] = useState<IDataModal<IMerchant>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<IMerchantRes, Error>({
    queryKey: ['get-merchant'],
    queryFn: getMerchant,
    retry: 0,
  });

  const actionMerchantMutation = useMutation({
    mutationFn: async (data: Partial<IMerchant>) =>
      tab === 'create' ? await addMerchant(data) : await updateMerchant(data),
    mutationKey: tab === 'create' ? ['add-merchant'] : ['update-merchant'],
    onSuccess: data => {
      if (data.status === 'fulfilled' && tab === 'create') {
        notification.success({ message: 'Sync Merchant สำเร็จ' });
        refetch();
      } else if (data?.data[0].status === 'fulfilled' && tab === 'update') {
        notification.success({ message: 'Update Merchant สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: data.reason?.errors?.errorMessage });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedMerchant = async (selectedRows: any) => {
    await actionMerchantMutation.mutateAsync(selectedRows);
  };

  const actionDeleteMerchant = useMutation({
    mutationFn: async (data: Partial<IMerchant>) => await deleteMerchant(data),
    mutationKey: ['delete-merchant'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete merchant สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete merchant ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeleteMerchant = async (selectedRows: any) => {
    await actionDeleteMerchant.mutateAsync(selectedRows);
  };

  const handleUpdateMerchantCode = useCallback((item: IMerchant) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IMerchant, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัสล้ง',
          dataIndex: 'CMAD_ADDR',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.CMAD_ADDR.replace(/\D/g, '').replace('-', '')) -
            Number(b.CMAD_ADDR.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.CMAD_ADDR)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.CMAD_ADDR?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อล้ง',
          dataIndex: 'CMAD_NAME',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.CMAD_NAME)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.CMAD_NAME?.startsWith(value as string);
          },
        },
        {
          title: 'ตำบล',
          dataIndex: 'CMAD_TUMBOL_NAME',
          filterSearch: true,
          filters: Array.from(
            new Set(selected.map(item => item.CMAD_TUMBOL_NAME)),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.CMAD_TUMBOL_NAME?.startsWith(value as string);
          },
        },
        {
          title: 'อำเภอ',
          dataIndex: 'CMAD_AMPHUR_NAME',
          filterSearch: true,
          filters: Array.from(
            new Set(selected.map(item => item.CMAD_AMPHUR_NAME)),
          )
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.CMAD_AMPHUR_NAME?.startsWith(value as string);
          },
        },
        {
          title: 'จังหวัด',
          dataIndex: 'CMAD_CITY',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.CMAD_CITY)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.CMAD_CITY?.startsWith(value as string);
          },
        },
      ] as TableColumnsType<IMerchant>,
    [tab, selected, handleUpdateMerchantCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IMerchant[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: IMerchant) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const itemCode = (item.CMAD_ADDR || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || itemCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeleteMerchant(selectedRows)}
            >
              {actionDeleteMerchant.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล Merchant'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedMerchant(selectedRows)}
          >
            {actionMerchantMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<IMerchant>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="land"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<IMerchant>
              label="เอกสาร"
              name="CMAD_ADDR"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(item => ({
                  label: `${item.CMAD_ADDR} - ${item.CMAD_ADDR}`,
                  value: item.CMAD_ADDR,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default MerchantPage;
